/**
 * Subscription Pricing Hook - Bridge between CMS and Subscription Screen
 * Phase CMS-2: Component Migration
 *
 * Purpose:
 * Provides a unified API for the subscription screen to access pricing data,
 * with automatic fallback to constants.ts when CMS is disabled.
 *
 * Key Differences from useTierPricing:
 * - Returns pricing by billing interval (for easy lookup)
 * - Maintains backward compatibility with PRICING constant structure
 * - Includes isLoading state for skeleton UI
 *
 * Usage:
 * ```tsx
 * const { getPriceForInterval, isLoading, isCMSEnabled } = useSubscriptionPricing(
 *   'drama_plus',
 *   'individual'
 * );
 *
 * // Get price for selected interval
 * const currentPrice = getPriceForInterval('month');
 * // Returns: { price: 9.99, displayPrice: '$9.99/mo', ... }
 * ```
 */

import { useMemo } from 'react';
import { useTierPricing } from './useCMS';
import { PRICING, type ProductKey } from '../../../lib/polar/constants';
import { isCMSEnabled, type TierId, type PlanType, type BillingInterval, type PricingProduct } from '@/lib/cms/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Simplified pricing structure for subscription screen.
 * Compatible with both CMS and constants.ts data.
 */
export interface SubscriptionPrice {
  /** Product key for checkout API (e.g., "drama_plus_monthly") */
  productKey: string;
  /** Price in dollars (e.g., 9.99) */
  price: number;
  /** Display price (e.g., "$9.99/mo") */
  displayPrice: string;
  /** Billed as text for longer intervals (e.g., "$24.99 every 3 months") */
  billedAs: string | null;
  /** Per person text for family plans (e.g., "$5/person") */
  perPerson: string | null;
  /** Daily quota for this plan */
  dailyQuota: number;
  /** Savings badge (e.g., "Save 10%") */
  savingsBadge: string | null;
  /** Promo badge (e.g., "⭐ Best Value") */
  promoBadge: string | null;
  /** Whether this interval should be highlighted */
  highlight: boolean;
}

/**
 * Pricing data indexed by billing interval.
 * Some intervals may be missing if not configured for the tier/plan combination.
 * Components should handle missing intervals gracefully (skip rendering or show fallback).
 *
 * @example
 * ```tsx
 * // Safe iteration - skip missing intervals
 * {Object.entries(pricingByInterval).map(([interval, price]) => (
 *   price && <PriceCard key={interval} price={price} />
 * ))}
 *
 * // Safe lookup - handle null
 * const monthlyPrice = pricingByInterval.month;
 * if (monthlyPrice) {
 *   // render price
 * }
 * ```
 */
export type PricingByInterval = Partial<Record<BillingInterval, SubscriptionPrice>>;

export interface UseSubscriptionPricingResult {
  /** Get pricing for a specific billing interval (returns null if not available) */
  getPriceForInterval: (interval: BillingInterval) => SubscriptionPrice | null;
  /**
   * All pricing options as a partial map.
   * Some intervals may be undefined if not configured for this tier/plan.
   * @see PricingByInterval for usage examples
   */
  pricingByInterval: PricingByInterval;
  /** Whether CMS data is still loading */
  isLoading: boolean;
  /** Whether CMS is enabled (for debug display) */
  isCMSEnabled: boolean;
  /** Error if CMS fetch failed (data still available via fallback) */
  error: Error | null;
}

// ============================================================================
// Interval Mapping
// ============================================================================

/**
 * Map billing interval to legacy suffix used in product keys.
 */
const INTERVAL_TO_SUFFIX: Record<BillingInterval, string> = {
  week: 'weekly',
  month: 'monthly',
  quarter: 'quarterly',
  semester: 'semester',
  year: 'yearly',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert CMS PricingProduct to SubscriptionPrice format.
 */
function cmsToSubscriptionPrice(product: PricingProduct): SubscriptionPrice {
  return {
    productKey: product.product_key,
    price: product.price_cents / 100,
    displayPrice: product.display_price,
    billedAs: product.billed_as,
    perPerson: product.per_person,
    dailyQuota: product.daily_quota ?? 0,
    savingsBadge: product.savings_badge,
    promoBadge: product.promo_badge,
    highlight: product.highlight,
  };
}

/**
 * Convert legacy constants PRICING entry to SubscriptionPrice format.
 */
function legacyToSubscriptionPrice(
  productKey: ProductKey,
  data: (typeof PRICING)[ProductKey]
): SubscriptionPrice {
  return {
    productKey,
    price: data.price,
    displayPrice: data.displayPrice,
    billedAs: 'billedAs' in data ? (data.billedAs as string) : null,
    perPerson: 'perPerson' in data ? (data.perPerson as string) : null,
    dailyQuota: data.quota,
    savingsBadge: 'savings' in data ? (data.savings as string) : null,
    promoBadge: 'badge' in data ? (data.badge as string) : null,
    highlight: false,
  };
}

/**
 * Build product key from tier, plan type, and interval.
 */
function buildProductKey(
  tierId: TierId,
  planType: PlanType,
  interval: BillingInterval
): ProductKey {
  const familySuffix = planType === 'family' ? '_family' : '';
  const intervalSuffix = INTERVAL_TO_SUFFIX[interval];
  return `${tierId}${familySuffix}_${intervalSuffix}` as ProductKey;
}

/**
 * Get pricing from legacy constants for all intervals.
 * Returns a partial record - some intervals may be missing if not configured
 * in constants.ts for the given tier/plan combination.
 */
function getLegacyPricing(
  tierId: TierId,
  planType: PlanType
): PricingByInterval {
  const intervals: BillingInterval[] = ['week', 'month', 'quarter', 'semester', 'year'];
  const result: PricingByInterval = {};

  for (const interval of intervals) {
    const productKey = buildProductKey(tierId, planType, interval);
    const data = PRICING[productKey];
    if (data) {
      result[interval] = legacyToSubscriptionPrice(productKey, data);
    }
  }

  return result;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for fetching subscription pricing with CMS/constants.ts fallback.
 *
 * @param tierId - Subscription tier ('drama_plus' or 'dramax')
 * @param planType - Plan type ('individual' or 'family')
 * @returns Pricing data with helper functions
 *
 * @example
 * ```tsx
 * function PricingSelector({ tier, planType }: Props) {
 *   const { getPriceForInterval, isLoading } = useSubscriptionPricing(tier, planType);
 *   const [selectedInterval, setSelectedInterval] = useState<BillingInterval>('month');
 *
 *   const currentPrice = getPriceForInterval(selectedInterval);
 *
 *   return (
 *     <View>
 *       {isLoading ? (
 *         <Skeleton />
 *       ) : (
 *         <Text>{currentPrice?.displayPrice}</Text>
 *       )}
 *     </View>
 *   );
 * }
 * ```
 */
export function useSubscriptionPricing(
  tierId: TierId,
  planType: PlanType = 'individual'
): UseSubscriptionPricingResult {
  // Always call the hook (React rules), but CMS client handles the feature flag internally
  const { data: cmsData, isLoading, error } = useTierPricing(tierId, planType);

  // Check if CMS is enabled (for display purposes)
  const cmsEnabled = isCMSEnabled();

  // Build pricing map from CMS data or fallback to constants
  // Returns a partial record - some intervals may be missing if not configured
  const pricingByInterval = useMemo<PricingByInterval>(() => {
    // If CMS is enabled and has data, use it
    if (cmsEnabled && cmsData && cmsData.length > 0) {
      const result: PricingByInterval = {};
      for (const product of cmsData) {
        result[product.billing_interval] = cmsToSubscriptionPrice(product);
      }
      return result;
    }

    // Fallback to legacy constants
    return getLegacyPricing(tierId, planType);
  }, [cmsEnabled, cmsData, tierId, planType]);

  // Helper to get pricing for a specific interval
  const getPriceForInterval = useMemo(
    () => (interval: BillingInterval): SubscriptionPrice | null => {
      return pricingByInterval[interval] ?? null;
    },
    [pricingByInterval]
  );

  return {
    getPriceForInterval,
    pricingByInterval,
    isLoading: cmsEnabled ? isLoading : false, // Only show loading when CMS is enabled
    isCMSEnabled: cmsEnabled,
    error: error ?? null,
  };
}
